'use client';

import React from 'react';
import { Box, Flex, Text } from '@skin-seoul/design-system';
import { formatCurrency, useCartService, useGetShippingPolicy, useRegionCurrency, type Country, CLASSNAME, getTiersForUser, inferIsFirstOrder, buildMilestonesFromTiers, calculateProgress, getBottomMessage } from '@skin-seoul/service';
import { CheckIcon } from './components/CheckIcon';
import { ConfettiAnimation } from './components/ConfettiAnimation';
import { BMSMGradient } from './components/BMSMGradient';
interface BMSMCampaignBannerProps {
  className?: string;
  /** Banner height in px */
  height?: number;
}
export function BMSMCampaignBanner({
  className = '',
  height = 130
}: BMSMCampaignBannerProps) {
  const {
    country,
    currency
  } = useRegionCurrency();
  const {
    useGetCartSuspenseQuery
  } = useCartService();
  const {
    shippingPolicy
  } = useGetShippingPolicy(country as Country);

  // Get cart data
  const {
    data: cartData
  } = useGetCartSuspenseQuery({
    currency
  });

  // Extract data
  const isAuthenticated = cartData?.isAuthenticated ?? false;
  const cartTotalPrice = cartData?.cart.totals.totalPrice ?? 0;
  const cartTotal = cartData?.cart.totals.total ?? 0;
  const cartTotalDiscount = cartData?.cart.totals.totalDiscount ?? 0;
  const shippingFee = shippingPolicy?.shippingFee ?? 0;
  const _isFreeShipping = cartData?.cart.shippingRates?.length !== 0 && cartData?.cart.shippingRates?.[0]?.price === 0;
  const coupons = cartData?.cart.coupons ?? [];

  // Get appropriate tiers based on user type
  const tiers = getTiersForUser(country as Country, isAuthenticated, false);
  const freeShippingThreshold = tiers.find(t => t.type === 'shipping')?.threshold ?? tiers[0]?.threshold ?? 0;

  // Infer if first order
  const isLikelyFirstOrder = inferIsFirstOrder(isAuthenticated, _isFreeShipping, cartTotalPrice, freeShippingThreshold, coupons);

  // Get correct tiers with first order inference
  const finalTiers = getTiersForUser(country as Country, isAuthenticated, isLikelyFirstOrder);

  // Build milestones
  const milestonesData = buildMilestonesFromTiers(finalTiers, cartTotalPrice, currency);

  // Override free shipping achievement if server says so
  const milestones = milestonesData.map(m => {
    if (_isFreeShipping && m.tier.type === 'shipping') {
      return {
        ...m,
        achieved: true,
        statusText: 'Available'
      };
    }
    return m;
  });

  // Calculate progress
  const baseProgress = calculateProgress(finalTiers, cartTotalPrice, _isFreeShipping);
  const progress = _isFreeShipping && milestones[0]?.tier.type === 'shipping' ? Math.max(baseProgress, milestones[0].anchorPercent * 100) : baseProgress;

  // Get bottom message
  const bottomMessage = getBottomMessage({
    isAuthenticated,
    isLikelyFirstOrder,
    isFreeShipping: _isFreeShipping,
    tiers: finalTiers,
    cartTotalPrice,
    shippingFee,
    cartTotalDiscount,
    currency
  });

  // Confetti animations
  const showFreeShippingConfetti = milestones[0]?.achieved ?? false;
  const showDiscountConfetti = milestones.find(m => m.tier.discountPercent === 5)?.achieved ?? false;

  // Layout constants
  const LABEL_AREA_HEIGHT_PX = 52;
  const ICON_SIZE_PX = 24;
  const BAR_HEIGHT_PX = 4;
  const CHECK_TEXT_GAP_PX = 12;
  return <Flex position="relative" width="100%" direction="column" bg="generic.100" overflow="hidden" alignItems="center" justifyContent="center" py="16px" minH={`${height}px`} height={`${height}px`} gap={2} borderRadius="16px" className={className} data-sentry-element="Flex" data-sentry-component="BMSMCampaignBanner" data-sentry-source-file="BMSMCampaignBanner.tsx">
      <BMSMGradient data-sentry-element="BMSMGradient" data-sentry-source-file="BMSMCampaignBanner.tsx" />

      <Flex zIndex={1} position="relative" width="100%" padding="0 16px" height={`${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX}px`} alignItems="center" justifyContent="center" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Flex" data-sentry-source-file="BMSMCampaignBanner.tsx">
        <Box position="absolute" left="0" right="0" bottom="0" height={`${ICON_SIZE_PX}px`} data-sentry-element="Box" data-sentry-source-file="BMSMCampaignBanner.tsx">
          {/* Progress Bar */}
          <Box position="absolute" left="16px" right="16px" top={`${(ICON_SIZE_PX - BAR_HEIGHT_PX) / 2}px`} height={`${BAR_HEIGHT_PX}px`} bg="neutral.600" borderRadius="2px" data-sentry-element="Box" data-sentry-source-file="BMSMCampaignBanner.tsx">
            <Box width={`${progress}%`} height="100%" bg="success.600" borderRadius="2px" transition="width 0.3s ease" data-sentry-element="Box" data-sentry-source-file="BMSMCampaignBanner.tsx" />
          </Box>

          {/* Milestones */}
          <Box position="absolute" left="16px" right="16px" top={`-${LABEL_AREA_HEIGHT_PX}px`} height={`${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX}px`} data-sentry-element="Box" data-sentry-source-file="BMSMCampaignBanner.tsx">
            {milestones.map((m, index) => {
            const clamped = Math.max(0, Math.min(1, m.anchorPercent));
            const left = (() => {
              if (clamped === 0) return '0%';
              if (clamped === 1) return '100%';
              return `${clamped * 100}%`;
            })();
            const translate = (() => {
              if (clamped === 0) return 'translateX(0)';
              if (clamped === 1) return 'translateX(-100%)';
              return 'translateX(-50%)';
            })();
            return <React.Fragment key={`${m.tier.type}-${index}`}>
                  <Flex position="absolute" left={left} transform={translate} bottom={`${ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px`} direction="column" gap="2px" align="center">
                    <Text textStyle="label.small" whiteSpace="nowrap" fontWeight="medium" color={m.achieved ? 'primary.200' : 'neutral.400'}>
                      {m.statusText}
                    </Text>
                    <Text textStyle="label.large" whiteSpace="nowrap" fontWeight="bold" color="generic.0" letterSpacing="-0.64px">
                      {m.title}
                    </Text>
                  </Flex>
                  <Box position="absolute" left={left} transform={translate} bottom="0" width="24px" height="24px">
                    <CheckIcon achieved={m.achieved} />
                  </Box>
                </React.Fragment>;
          })}
          </Box>
        </Box>
      </Flex>

      <ConfettiAnimation showFreeShippingConfetti={showFreeShippingConfetti} showDiscountConfetti={showDiscountConfetti} data-sentry-element="ConfettiAnimation" data-sentry-source-file="BMSMCampaignBanner.tsx" />

      {/* Bottom Message */}
      <Flex align="center" gap="2px" width="100%" zIndex={1} justify="center" data-sentry-element="Flex" data-sentry-source-file="BMSMCampaignBanner.tsx">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="2" viewBox="0 0 22 2" fill="none" data-sentry-element="svg" data-sentry-source-file="BMSMCampaignBanner.tsx">
          <path d="M21 1H1" stroke="url(#paint0_linear_2188_5477)" strokeLinecap="round" data-sentry-element="path" data-sentry-source-file="BMSMCampaignBanner.tsx" />
          <defs data-sentry-element="defs" data-sentry-source-file="BMSMCampaignBanner.tsx">
            <linearGradient id="paint0_linear_2188_5477" x1="21" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" data-sentry-element="linearGradient" data-sentry-source-file="BMSMCampaignBanner.tsx">
              <stop stopColor="white" data-sentry-element="stop" data-sentry-source-file="BMSMCampaignBanner.tsx" />
              <stop offset="1" stopOpacity="0" data-sentry-element="stop" data-sentry-source-file="BMSMCampaignBanner.tsx" />
            </linearGradient>
          </defs>
        </svg>

        <Text className={CLASSNAME.NOT_TRANSLATE} textStyle="label.xsmall" whiteSpace="nowrap" fontWeight="medium" color="generic.0" textAlign="center" data-sentry-element="Text" data-sentry-source-file="BMSMCampaignBanner.tsx">
          {bottomMessage}
        </Text>

        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="2" viewBox="0 0 22 2" fill="none" data-sentry-element="svg" data-sentry-source-file="BMSMCampaignBanner.tsx">
          <path d="M1 1H21" stroke="url(#paint0_linear_2188_5479)" strokeLinecap="round" data-sentry-element="path" data-sentry-source-file="BMSMCampaignBanner.tsx" />
          <defs data-sentry-element="defs" data-sentry-source-file="BMSMCampaignBanner.tsx">
            <linearGradient id="paint0_linear_2188_5479" x1="1" y1="2" x2="21" y2="2" gradientUnits="userSpaceOnUse" data-sentry-element="linearGradient" data-sentry-source-file="BMSMCampaignBanner.tsx">
              <stop stopColor="white" data-sentry-element="stop" data-sentry-source-file="BMSMCampaignBanner.tsx" />
              <stop offset="1" stopOpacity="0" data-sentry-element="stop" data-sentry-source-file="BMSMCampaignBanner.tsx" />
            </linearGradient>
          </defs>
        </svg>
      </Flex>
    </Flex>;
}